import React from 'react';
import { ThemeProvider } from 'styled-components';
import { checkValue } from '../helper';
import { ThemeColorMode, ThemeType } from '../module-util';
import {
  BrandExpediaLightToken,
  BrandExpediaDarkToken,
  BrandHotelsLightToken,
  BrandHotelsDarkToken,
  BrandVrboLightToken,
  BrandVrboDarkToken,
  MesoExpediaLightToken,
  MesoExpediaDarkToken,
  MesoHotelsLightToken,
  MesoHotelsDarkToken,
  MesoVrboLightToken,
  MesoVrboDarkToken,
} from '../cpds/token';

const defaultBrand = 'expedia';

interface ExStylesProps {
  [key: string]: string | any | Array<any>;
}
interface ThemeProps {
  children: React.ReactNode;
  brand?: string;
  exStyles?: ExStylesProps | Array<ExStylesProps>;
  exClass?: string;
  themeColorMode?: ThemeColorMode;
  themeType?: ThemeType;
}

const themeStyles = {
  expedia: {
    colors: {
      heading: '#141D38',
      subHeading: '#000000',
    },
    fontFamily:
      '"Centra No2", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeight: {
      w1: '100',
      w2: '200',
      w3: '300',
      w4: '400',
      w5: '500',
      w6: '600',
      w7: '700',
      w8: '800',
      w9: '900',
      normal: 'normal',
      bold: 'bold',
      bolder: 'bolder',
      default: 'normal',
    },
    fontSynthesis: 'initial',
    cpds: {
      brand: {
        light: { ...BrandExpediaLightToken.cpds },
        dark: { ...BrandExpediaDarkToken.cpds },
      },
      meso: {
        light: { ...MesoExpediaLightToken.cpds },
        dark: { ...MesoExpediaDarkToken.cpds },
      },
    },
  },
  hotels: {
    colors: {
      heading: 'red',
      subHeading: 'green',
    },
    fontFamily:
      'system, -apple-system, BlinkMacSystemFont, ".SFNSText-Regular", "San Francisco", Roboto, "Segoe UI", "Helvetica Neue", "Lucida Grande", sans-serif',
    fontWeight: {
      w1: '100',
      w2: '200',
      w3: '300',
      w4: '400',
      w5: '500',
      w6: '600',
      w7: '700',
      w8: '800',
      w9: '900',
      normal: 'normal',
      bold: 'bold',
      bolder: 'bolder',
      default: 'normal',
    },
    cpds: {
      brand: {
        light: { ...BrandHotelsLightToken.cpds },
        dark: { ...BrandHotelsDarkToken.cpds },
      },
      meso: {
        light: { ...MesoHotelsLightToken.cpds },
        dark: { ...MesoHotelsDarkToken.cpds },
      },
    },
  },
  vrbo: {
    // this value using in modules (featured gallery carousel)
    fontWeight: {
      w5: '400',
      w7: '700',
    },
    cpds: {
      brand: {
        light: { ...BrandVrboLightToken.cpds },
        dark: { ...BrandVrboDarkToken.cpds },
      },
      meso: {
        light: { ...MesoVrboLightToken.cpds },
        dark: { ...MesoVrboDarkToken.cpds },
      },
    },
  },
  meso: {
    cpds: {
      light: { ...MesoExpediaLightToken.cpds },
      dark: { ...MesoExpediaLightToken.cpds },
    },
  },
};

const themeEvents = (themeStyle) => {
  const getFontWeight = (key: string) => {
    let fontWeight = themeStyle.fontWeight[key];
    if (!fontWeight) {
      fontWeight = themeStyle.fontWeight['default'];
    }
    return fontWeight;
  };

  return { getFontWeight };
};

const getTheme = (
  brand: string,
  themeColorMode: ThemeColorMode,
  themeType: ThemeType,
  exStyles: ExStylesProps | Array<ExStylesProps>
) => {
  let theme = themeStyles[brand];
  theme = checkValue(theme) ? theme : themeStyles[defaultBrand]; 
  // need to check if brand is not available
  theme = { ...theme, cpds: { ...theme.cpds[themeType], themeColorMode: themeColorMode, themeType: themeType, brand }, ...exStyles };

  return {
    styles: theme,
    styleEvents: themeEvents(theme),
  };
};

const Theme: React.FC<ThemeProps> = (props) => {
  const { children, brand = '', exStyles, themeColorMode = ThemeColorMode.Light, themeType = ThemeType.Meso } = props;

  const themeProps = getTheme(brand, themeColorMode, themeType, exStyles);

  return <ThemeProvider theme={themeProps}>{children}</ThemeProvider>;
};

export { ExStylesProps, ThemeProps, Theme, getTheme };
